import { ChangeEvent, useState } from 'react';
import { Amplitude, Events } from '@iwoca/frontend-tracking-library';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Label, Message, Select } from '@iwoca/orion';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useGetDirectorSearch } from 'lendingApi/lendingApiHooks';
import _ from 'lodash';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { DirectorModal } from './DirectorModal';
import styles from './DirectorSearch.module.css';

type TProps = {
  onChange: (
    person?: GetApiIntroducerPortalDirectorSearchResponse['data'][number],
  ) => void;
  label: string;
  companyNumber?: string;
  showCannotFindApplicantModal?: boolean;
  error?: string;
  disabled?: boolean;
};

export const DirectorSearch = ({
  onChange,
  label,
  companyNumber,
  showCannotFindApplicantModal = true,
  error,
  disabled: inputDisabled,
}: TProps) => {
  const { locale } = useLocale();
  const { trackEvent } = useAmplitude();
  const [selectedOption, setSelectedOption] = useState('');
  const [showDirectorModal, setShowDirectorModal] = useState(false);

  const { data: directorsData, isLoading } =
    useGetDirectorSearch(companyNumber);
  const directorOptions = directorsData?.data;

  const disabled = isLoading || !companyNumber || inputDisabled;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    // Can non-null associatedPeople as they can only select
    // if the options exist
    const director = directorOptions![parseInt(e.target.value, 10)];

    trackEvent(AMPLITUDE_EVENTS.directorSelected, {
      'Director name': `${director.first_name} ${director.last_name}`,
    });

    onChange(director);
    setSelectedOption(e.target.value);
  };

  const renderOptions = () => {
    if (!directorOptions) {
      return <option disabled>No directors found</option>;
    } else
      return directorOptions?.map((person, index) => (
        <option key={index} value={index}>
          {_.upperCase(person.last_name)}, {person.first_name}{' '}
          {person.roles?.includes('director') && '(Director)'}
        </option>
      ));
  };

  return (
    <>
      <div className={styles.labelContainer}>
        <Label htmlFor="director-search">{label}</Label>
        {showCannotFindApplicantModal && (
          <ButtonLink
            type="button"
            className={styles.showDirectorModalButton}
            data-testid="manual-address-btn"
            onClick={() => {
              setShowDirectorModal(true);
              Amplitude.track(
                Events.introducerPortal.introducerPortalEventCreator({
                  event: AMPLITUDE_EVENTS.modalOpened,
                  locale,
                  payload: {
                    'Page name': document.title,
                    URL: window.location.pathname,
                    'Modal name': 'Applicant not listed',
                  },
                }),
              );
            }}
          >
            I can’t find the applicant's name on the list
          </ButtonLink>
        )}
      </div>
      <Select
        defaultValue={selectedOption}
        onChange={handleChange}
        disabled={disabled}
        id="director-search"
      >
        <option value="" disabled>
          Choose an option
        </option>
        {renderOptions()}
      </Select>
      {showDirectorModal && (
        <DirectorModal
          showModal={showDirectorModal}
          closeModal={() => setShowDirectorModal(false)}
        />
      )}

      {error && (
        <Message className={styles.error} message={error} type="error" />
      )}
    </>
  );
};
